import * as React from 'react';

import TextField, { TextFieldProps } from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { OptionalDate } from '../../pages/PerDiemArchive/components/selectors/date-selector/types';

export interface DateSelectorBaseProps {
  value: OptionalDate;
}

interface DateSelectorProps extends DateSelectorBaseProps {
  label: string;
  minDate?: OptionalDate;
  maxDate?: OptionalDate;
  errorMessage?: string;
  onChange: (date: OptionalDate) => void;
}

export const DateSelector: React.FC<DateSelectorProps> = (props) => {
  const { label, onChange, minDate, maxDate, errorMessage, value } = props;

  const [state, setState] = React.useState<{ error?: string }>({});

  const handleChange = (date: OptionalDate) => {
    if (!date?.isValid()) {
      setState((s) => ({
        ...s,
        date: null,
        error: undefined,
      }));
      onChange(null);
      return;
    }

    const sanitizedDate = date.startOf('day');

    if (
      (maxDate && sanitizedDate.isAfter(maxDate)) ||
      (minDate && sanitizedDate.isBefore(minDate))
    ) {
      setState((s) => ({
        ...s,
        date: null,
        error: errorMessage || 'Invalid date',
      }));
      onChange(null);
      return;
    }

    setState((s) => ({ ...s, date: sanitizedDate, error: undefined }));
    onChange(sanitizedDate);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DesktopDatePicker
        inputFormat="MM/DD/yyyy"
        minDate={minDate || undefined}
        maxDate={maxDate || undefined}
        value={value}
        onChange={handleChange}
        InputProps={{ className: 'shadow-sm' }}
        renderInput={(params: TextFieldProps) => (
          <TextField
            {...params}
            inputProps={{
              style: { fontSize: 16 },
              ...params.inputProps,
            }}
            helperText={state.error}
            error={!!state.error}
            fullWidth
            InputLabelProps={{
              shrink: true,
              className: 'relative transform-none text-gray-900 font-medium pb-1',
            }}
            label={label}
          />
        )}
      />
    </LocalizationProvider>
  );
};
